import React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ToggleRow from "../../../common/ToggleRow";

export default function ChatUxTab(props) {
  const {
    isSystemAgent,
    description,
    showSystemPromptInChat,
    setShowSystemPromptInChat,
    showToolCallsAndOutputsInChat,
    setShowToolCallsAndOutputsInChat,
    showInterAgentMessagesInChat,
    setShowInterAgentMessagesInChat,
    showWorkflowProvenanceInChat,
    setShowWorkflowProvenanceInChat,
    showSchedulingActivityInChat,
    setShowSchedulingActivityInChat,
    showSessionOperationalMetaInChat,
    setShowSessionOperationalMetaInChat,
    showSupervisorControlMessagesInChat,
    setShowSupervisorControlMessagesInChat,
    enableHumanTakeoverMode,
    setEnableHumanTakeoverMode,
    examplePrompts,
    setExamplePrompts,
  } = props;

  return (
    <>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Chat Transcript Visibility
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
        Control what end users can see in the chat transcript. These settings do
        not change what is included in the AI context/loop.
      </Typography>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Example Prompts
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 1.5 }}>
        Shown as clickable cards under the new-chat textfield. Clicking a card
        inserts the prompt without sending it.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {examplePrompts.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            No example prompts configured.
          </Typography>
        )}
        {examplePrompts.map((example, index) => (
          <Box
            key={`${index}-${example.prompt}`}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "180px 1fr auto" },
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <TextField
              size="small"
              label="Card title"
              value={example.title || ""}
              disabled={isSystemAgent}
              onChange={(event) =>
                setExamplePrompts((prev) =>
                  prev.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, title: event.target.value }
                      : item
                  )
                )
              }
            />
            <TextField
              size="small"
              label="Prompt inserted into chat"
              value={example.prompt || ""}
              disabled={isSystemAgent}
              multiline
              minRows={2}
              onChange={(event) =>
                setExamplePrompts((prev) =>
                  prev.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, prompt: event.target.value }
                      : item
                  )
                )
              }
            />
            {!isSystemAgent && (
              <IconButton
                size="small"
                onClick={() =>
                  setExamplePrompts((prev) =>
                    prev.filter((_, itemIndex) => itemIndex !== index)
                  )
                }
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        ))}
        {!isSystemAgent && examplePrompts.length < 6 && (
          <Button
            variant="outlined"
            onClick={() =>
              setExamplePrompts((prev) => [...prev, { title: "", prompt: "" }])
            }
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Add example prompt
          </Button>
        )}
      </Box>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <ToggleRow
          checked={showSystemPromptInChat}
          onChange={setShowSystemPromptInChat}
          title="Show system prompt block in chat UI"
          description="Off hides the expandable system prompt section from responders."
        />
        <ToggleRow
          checked={showToolCallsAndOutputsInChat}
          onChange={setShowToolCallsAndOutputsInChat}
          title="Show tool calls and tool outputs in chat UI"
          description="Off hides internal tool activity cards while preserving form interactions."
        />
        <ToggleRow
          checked={showInterAgentMessagesInChat}
          onChange={setShowInterAgentMessagesInChat}
          title="Show inter-agent routing messages"
          description="Off hides agent-to-agent message cards and routing chatter from responders."
        />
        <ToggleRow
          checked={showWorkflowProvenanceInChat}
          onChange={setShowWorkflowProvenanceInChat}
          title="Show workflow provenance cards"
          description="Off hides workflow/source context wrappers and keeps the transcript focused on plain conversation content."
        />
        <ToggleRow
          checked={showSchedulingActivityInChat}
          onChange={setShowSchedulingActivityInChat}
          title="Show scheduling activity"
          description="Off hides schedule-message tool actions and timing activity cards."
        />
        <ToggleRow
          checked={showSessionOperationalMetaInChat}
          onChange={setShowSessionOperationalMetaInChat}
          title="Show session operational metadata"
          description="Off hides session email/thread metadata and other operational UI details."
        />
        <ToggleRow
          checked={showSupervisorControlMessagesInChat}
          onChange={setShowSupervisorControlMessagesInChat}
          title="Show supervisor control messages"
          description="Off hides supervisor review/takeover control messages from responder transcripts."
        />
        <ToggleRow
          checked={enableHumanTakeoverMode}
          onChange={setEnableHumanTakeoverMode}
          title="Enable human takeover mode"
          description="Lets operators pause AI responses per session in internal chat UI so humans can converse directly."
        />
      </Box>
    </>
  );
}
